/**
 * CategoryChart - Single category card with grouped bars per currency.
 * Uses same currency colors as the main spending chart.
 */

import React, { useMemo } from 'react';
import { BarChart, Bar, ResponsiveContainer, Tooltip } from 'recharts';
import {
    CategorySpending,
    EXPENSE_CATEGORIES,
    CURRENCY_RATES_TO_TRY,
} from '../../../services/documentApi';

// Same currency colors as SpendingChart
const CURRENCY_COLORS: Record<string, string> = {
    TRY: '#3b82f6',
    EUR: '#10b981',
    CHF: '#ef4444',
    USD: '#f59e0b',
    GBP: '#8b5cf6',
};

interface CategoryChartProps {
    categoryName: string;
    series: CategorySpending[];  // multiple currency series for same category
}

const MiniTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    const hasValue = payload.some((e: any) => e.value > 0);
    if (!hasValue) return null;

    return (
        <div className="dashboard__chart-tooltip">
            {payload
                .filter((entry: any) => entry.value > 0)
                .map((entry: any) => {
                    const cur = entry.dataKey;
                    const rate = CURRENCY_RATES_TO_TRY[cur] || 1;
                    const original = entry.value / rate;
                    return (
                        <div key={cur} className="dashboard__chart-tooltip-row">
                            <span className="dashboard__chart-tooltip-dot" style={{ backgroundColor: entry.color }} />
                            <span>{original.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {cur}</span>
                        </div>
                    );
                })}
        </div>
    );
};

const CategoryChart: React.FC<CategoryChartProps> = ({ categoryName, series }) => {
    const label = EXPENSE_CATEGORIES[categoryName]?.en || categoryName;

    const { chartData, currencies, totals } = useMemo(() => {
        const dateMap: Record<string, Record<string, number>> = {};
        const currencySet = new Set<string>();
        const totalsByCur: Record<string, number> = {};

        for (const cs of series) {
            currencySet.add(cs.currency);
            totalsByCur[cs.currency] = cs.category_total;
            const rate = CURRENCY_RATES_TO_TRY[cs.currency] || 1;
            for (const point of cs.time_series) {
                if (!dateMap[point.date]) dateMap[point.date] = {};
                dateMap[point.date][cs.currency] = point.total * rate;
            }
        }

        const sortedDates = Object.keys(dateMap).sort();
        const allCurrencies = Array.from(currencySet);
        const merged = sortedDates.map((date) => {
            const row: Record<string, any> = { date };
            for (const cur of allCurrencies) {
                row[cur] = dateMap[date][cur] ?? undefined;
            }
            return row;
        });

        return { chartData: merged, currencies: allCurrencies, totals: totalsByCur };
    }, [series]);

    return (
        <div className="dashboard__category-card">
            <div className="dashboard__category-header">
                <span className="dashboard__category-name">{label}</span>
                <div className="dashboard__category-totals">
                    {currencies.map((cur) => (
                        <span key={cur} className="dashboard__category-cur" style={{ color: CURRENCY_COLORS[cur] || '#6b7280' }}>
                            {(totals[cur] || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {cur}
                        </span>
                    ))}
                </div>
            </div>
            <div className="dashboard__category-chart">
                <ResponsiveContainer width="100%" height={80}>
                    <BarChart data={chartData} maxBarSize={12}>
                        <Tooltip content={<MiniTooltip />} />
                        {currencies.map((cur) => (
                            <Bar
                                key={cur}
                                dataKey={cur}
                                fill={CURRENCY_COLORS[cur] || '#6b7280'}
                                radius={[2, 2, 0, 0]}
                                minPointSize={2}
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default CategoryChart;
